import type { ServerFeature } from '@affine/graphql';
import {
  backoffRetry,
  effect,
  Entity,
  fromPromise,
  LiveData,
  onComplete,
  onStart,
} from '@toeverything/infra';
import { exhaustMap, map } from 'rxjs';

import { ServerScope } from '../scopes/server';
import { AuthService } from '../services/auth';
import { FetchService } from '../services/fetch';
import { GraphQLService } from '../services/graphql';
import { ServerConfigStore } from '../stores/server-config';
import type { ServerListStore } from '../stores/server-list';
import type { ServerConfig, ServerMetadata } from '../types';

type LowercaseServerFeature = Lowercase<ServerFeature>;
type ServerFeatureRecord = {
  [key in LowercaseServerFeature]: boolean;
};

type RemoteServerConfig = Awaited<
  ReturnType<ServerConfigStore['fetchServerConfig']>
>;

function isAbortError(error: unknown): boolean {
  if (error instanceof Event && error.type === 'abort') {
    return true;
  }

  if (error instanceof DOMException && error.name === 'AbortError') {
    return true;
  }

  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    error.name === 'AbortError'
  );
}

export class Server extends Entity<{
  serverMetadata: ServerMetadata;
}> {
  readonly id = this.props.serverMetadata.id;
  readonly baseUrl = this.props.serverMetadata.baseUrl;
  /** sl-m3s8: Dedicated WebSocket URL. Getter so it picks up config updates. */
  get syncUrl(): string {
    return (
      this.serverMetadata.syncUrl ?? this.config$.value?.syncUrl ?? this.baseUrl
    );
  }
  readonly scope = this.framework.createScope(ServerScope, {
    server: this as Server,
  });

  readonly serverConfigStore = this.scope.framework.get(ServerConfigStore);
  readonly fetch = this.scope.framework.get(FetchService).fetch;
  readonly gql = this.scope.framework.get(GraphQLService).gql;
  get account$() {
    return this.scope.framework.get(AuthService).session.account$;
  }
  get cachedAccountId() {
    return this.scope.framework.get(AuthService).cachedAccountId;
  }
  get confirmedAccountId() {
    return this.scope.framework.get(AuthService).confirmedAccountId;
  }
  get confirmedAccountId$() {
    return this.scope.framework.get(AuthService).confirmedAccountId$;
  }
  readonly serverMetadata = this.props.serverMetadata;

  constructor(private readonly serverListStore: ServerListStore) {
    super();
  }

  readonly config$ = LiveData.from<ServerConfig>(
    this.serverListStore.watchServerConfig(this.serverMetadata.id).pipe(
      map(config => {
        if (!config) {
          throw new Error('Failed to load server config');
        }
        return config;
      })
    ),
    this.serverListStore.getServerConfig(this.serverMetadata.id) ??
      (null as any)
  );

  readonly isConfigRevalidating$ = new LiveData(false);

  readonly features$ = this.config$.map(config => {
    return Array.from(new Set(config.features)).reduce((acc, cur) => {
      acc[cur.toLowerCase() as LowercaseServerFeature] = true;
      return acc;
    }, {} as ServerFeatureRecord);
  });

  readonly credentialsRequirement$ = this.config$.map(config => {
    return config ? config.credentialsRequirement : null;
  });

  private applyRemoteConfig(config: RemoteServerConfig) {
    this.serverListStore.updateServerConfig(this.serverMetadata.id, {
      credentialsRequirement: config.credentialsRequirement,
      features: config.features,
      oauthProviders: config.oauthProviders,
      serverName: config.name,
      type: config.type,
      version: config.version,
      initialized: config.initialized,
      syncUrl: config.syncUrl ?? undefined,
    });
    // sl-m3s8: Propagate syncUrl from serverConfig to metadata
    if (config.syncUrl) {
      this.serverMetadata.syncUrl = config.syncUrl;
    }
  }

  private async fetchAndApplyRemoteConfig(signal?: AbortSignal) {
    const config = await this.serverConfigStore.fetchServerConfig(
      this.baseUrl,
      signal
    );
    this.applyRemoteConfig(config);
  }

  private remoteConfigRevalidation: Promise<void> | null = null;
  private hasCompletedRemoteConfigRevalidation = false;

  private getRemoteConfigRevalidation(force = false) {
    if (this.remoteConfigRevalidation) {
      return this.remoteConfigRevalidation;
    }
    if (!force && this.hasCompletedRemoteConfigRevalidation) {
      return Promise.resolve();
    }
    this.remoteConfigRevalidation = this.fetchAndApplyRemoteConfig()
      .then(() => {
        this.hasCompletedRemoteConfigRevalidation = true;
      })
      .finally(() => {
        this.remoteConfigRevalidation = null;
      });
    return this.remoteConfigRevalidation;
  }

  private waitForCompletionOrAbort(
    promise: Promise<void>,
    signal?: AbortSignal
  ) {
    if (!signal) {
      return promise;
    }
    if (signal.aborted) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve, reject) => {
      const cleanup = () => signal.removeEventListener('abort', onAbort);
      const onAbort = () => {
        cleanup();
        resolve();
      };
      signal.addEventListener('abort', onAbort, { once: true });
      promise.then(
        () => {
          cleanup();
          resolve();
        },
        error => {
          cleanup();
          reject(error);
        }
      );
    });
  }

  readonly revalidateConfig = effect(
    exhaustMap(() => {
      return fromPromise(signal =>
        this.waitForCompletionOrAbort(
          this.getRemoteConfigRevalidation(true),
          signal
        )
      ).pipe(
        backoffRetry({
          count: Infinity,
        }),
        onStart(() => {
          this.isConfigRevalidating$.next(true);
        }),
        onComplete(() => {
          this.isConfigRevalidating$.next(false);
        })
      );
    })
  );

  async waitForConfigRevalidation(signal?: AbortSignal) {
    try {
      if (signal?.aborted) {
        return;
      }
      this.isConfigRevalidating$.next(true);
      await this.waitForCompletionOrAbort(
        this.getRemoteConfigRevalidation(),
        signal
      );
    } catch (error) {
      if (isAbortError(error) || signal?.aborted) {
        return;
      }
      console.error('Config revalidation failed:', error);
    } finally {
      this.isConfigRevalidating$.next(false);
    }
  }

  override dispose(): void {
    this.scope.dispose();
    this.revalidateConfig.unsubscribe();
  }
}
